import { useState, useEffect, useRef } from 'react';
import { useLazyCheckSlugAvailabilityQuery } from 'store/services/profile/profileApi';

export type SlugStatus = 'idle' | 'validating' | 'available' | 'unavailable' | 'invalid';

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function validateSlugFormat(slug: string): { valid: boolean; message: string } {
  if (slug.length < 3) {
    return { valid: false, message: 'Must be at least 3 characters' };
  }
  if (slug.length > 30) {
    return { valid: false, message: 'Must be 30 characters or fewer' };
  }
  if (!SLUG_REGEX.test(slug)) {
    return { valid: false, message: 'Letters, numbers, and hyphens only. Cannot start or end with a hyphen.' };
  }
  return { valid: true, message: '' };
}

export function useDebouncedSlugCheck(slug: string, currentSlug: string | null) {
  const [status, setStatus] = useState<SlugStatus>('idle');
  const [message, setMessage] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [checkAvailability] = useLazyCheckSlugAvailabilityQuery();

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    // If empty or same as current slug, reset
    if (!slug || slug === currentSlug) {
      setStatus('idle');
      setMessage('');
      return;
    }

    // Client-side validation first
    const validation = validateSlugFormat(slug);
    if (!validation.valid) {
      setStatus('invalid');
      setMessage(validation.message);
      return;
    }

    // Debounced server check
    setStatus('validating');
    setMessage('');
    timerRef.current = setTimeout(async () => {
      try {
        const result = await checkAvailability(slug).unwrap();
        setStatus(result.available ? 'available' : 'unavailable');
        setMessage(result.available ? 'Available!' : 'This URL is not available');
      } catch {
        setStatus('unavailable');
        setMessage('Error checking availability');
      }
    }, 500);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [slug, currentSlug, checkAvailability]);

  return { status, message };
}
